import React, { useState } from 'react';
import { Loader2, Wand2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const PRESETS = [
  ['icon_only', 'Icon only'], ['simpler', 'Simpler'], ['recolor', 'Change colors'], ['bw', 'Black & white'],
  ['no_tagline', 'Remove tagline'], ['horizontal', 'Horizontal'], ['stacked', 'Stacked'],
];

export default function ReviseLogoDialog({ open, onClose, imgUrl, palettes = [], onSubmit }) {
  const [preset, setPreset] = useState('');
  const [paletteIndex, setPaletteIndex] = useState(0);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit({ preset: preset || 'custom', instruction, palette_index: paletteIndex }); setPreset(''); setInstruction(''); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="revise-logo-dialog">
        <DialogHeader>
          <DialogTitle>Revise this logo</DialogTitle>
          <DialogDescription>We keep the same logo and only change what you ask. Each revision is saved as a new version.</DialogDescription>
        </DialogHeader>
        {imgUrl && <img src={imgUrl} alt="Logo to revise" className="w-full max-h-44 object-contain rounded-lg border border-[#E2E8F0] bg-white" />}
        <div>
          <div className="text-xs font-bold uppercase text-[#64748B] mb-1.5">Quick tweaks</div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPreset(preset === k ? '' : k)} data-testid={`revise-preset-${k}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${preset === k ? 'bg-[#F97316] text-white border-[#F97316]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#F97316]'}`}>{l}</button>
            ))}
          </div>
        </div>
        {preset === 'recolor' && palettes.length > 0 && (
          <div className="grid grid-cols-2 gap-2" data-testid="revise-palettes">
            {palettes.map((p, i) => (
              <button key={i} type="button" onClick={() => setPaletteIndex(i)} data-testid={`revise-palette-${i}`}
                className={`rounded-lg border-2 p-1.5 text-left ${paletteIndex === i ? 'border-[#1D4ED8]' : 'border-[#E2E8F0]'}`}>
                <div className="flex h-5 rounded overflow-hidden">{(p.colors || []).map((c, j) => <span key={j} className="flex-1" style={{ background: c.hex }} />)}</div>
                <div className="text-[10px] font-semibold text-[#475569] mt-1 truncate">{p.name}</div>
              </button>
            ))}
          </div>
        )}
        <label className="block text-xs font-bold uppercase text-[#64748B]">Or describe your change
          <textarea rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder='e.g. "make the cat face left, darker green"' data-testid="revise-instruction"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-normal normal-case focus:border-[#F97316] outline-none" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#475569] hover:bg-slate-100">Cancel</button>
          <button type="button" onClick={submit} disabled={busy || (!preset && !instruction.trim())} data-testid="revise-submit"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Revise logo
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
